import { SchemaField, SchemaFieldType } from 'features/data-explorer/types'
import {
  getSpatialDataSettingsFromSchema,
  getSpatialDataTypeFromColumnName,
  spatialDataTypeCompatible
} from './spatialDataSettings'
import { KeplerTable, MapDatasetEnriched, SpatialDataType } from '../types'
import { caseInsensitiveEquals } from 'features/common/utils/strings'
import { getSchemeFromTilesetDataset, isStaticSpatialIndexDataset } from '../state/builderSliceUtils'
import { FEATURE_GEOM_PROPERTY } from '@carto/api-client'
import { MapType } from 'features/maps/types'

export function formatGeoColumn({
  spatialDataColumn,
  spatialDataType
}: {
  spatialDataColumn: string
  spatialDataType: SpatialDataType
}) {
  if (spatialDataType === SpatialDataType.Geo) {
    return spatialDataColumn
  }
  return `${spatialDataType}:${spatialDataColumn}`
}

type ParsedGeoColumn = { spatialDataType: SpatialDataType; spatialDataColumn: string; hasSpatialIndex: boolean }

export function parseGeoColumn(geoColumn: string | null | undefined): ParsedGeoColumn | null {
  if (!geoColumn) {
    return null
  }
  const parts = geoColumn.split(':')
  if (parts.length === 1) {
    const spatialDataType = getSpatialDataTypeFromColumnName(geoColumn)
    return {
      spatialDataType: getSpatialDataTypeFromColumnName(geoColumn),
      spatialDataColumn: geoColumn,
      hasSpatialIndex: spatialDataType !== SpatialDataType.Geo
    }
  } else {
    const spatialDataType = parts[0] === 'geom' ? SpatialDataType.Geo : (parts[0].toLowerCase() as SpatialDataType)
    return {
      spatialDataType,
      spatialDataColumn: parts[1],
      hasSpatialIndex: spatialDataType !== SpatialDataType.Geo
    }
  }
}

export function parseGeoColumnSafe(geoColumn: string | null | undefined) {
  const parsedGeoColumn = parseGeoColumn(geoColumn)
  if (!parsedGeoColumn) {
    return {
      spatialDataType: undefined,
      spatialDataColumn: undefined,
      hasSpatialIndex: false
    }
  }
  return parsedGeoColumn
}

export function guessGeoColumnFromSchema(schema: SchemaField[]): string | null {
  const spatialDataSettings = getSpatialDataSettingsFromSchema(schema)
  if (!spatialDataSettings) return null

  return formatGeoColumn(spatialDataSettings)
}

export function isGeoColumnValid(geoColumn: string, schema: SchemaField[]): boolean {
  const spatialDataSettings = parseGeoColumn(geoColumn)
  if (!spatialDataSettings) return false

  return schema.some(
    (field) =>
      caseInsensitiveEquals(field.name, spatialDataSettings.spatialDataColumn) &&
      spatialDataTypeCompatible(field.type, spatialDataSettings.spatialDataType)
  )
}

export function getSourceSpatialDataType(
  geoColumn: string | null | undefined,
  schema: SchemaField[]
): SpatialDataType | undefined {
  if (!geoColumn) return undefined

  const spatialDataSettings = parseGeoColumn(geoColumn)
  if (!spatialDataSettings) return undefined

  const field = schema.find((field) => caseInsensitiveEquals(field.name, spatialDataSettings.spatialDataColumn))
  if (!field) {
    return spatialDataSettings.spatialDataType
  }
  if (field.type === SchemaFieldType.Geometry) {
    return SpatialDataType.Geo
  } else {
    return spatialDataSettings.spatialDataType
  }
}

export function getDatasetSourceSpatialDataType(keplerDataset: KeplerTable) {
  const geoColumn: string | undefined = keplerDataset?.metadata?.geoColumn
  const schema: SchemaField[] | undefined = keplerDataset?.metadata?.schema

  return geoColumn && schema ? getSourceSpatialDataType(geoColumn, schema) : undefined
}

export function isDynamicAggregatedPointDataset(keplerDataset: KeplerTable): boolean {
  const geoColumn: string | undefined = keplerDataset?.metadata?.geoColumn
  const schema: SchemaField[] | undefined = keplerDataset?.metadata?.schema

  if (!geoColumn || !schema) return false
  const spatialDataSettings = parseGeoColumn(geoColumn)
  if (!spatialDataSettings) return false

  const field = schema.find((field) => caseInsensitiveEquals(field.name, spatialDataSettings.spatialDataColumn))
  if (!field) return false

  return (
    field.type === SchemaFieldType.Geometry &&
    (spatialDataSettings.spatialDataType === SpatialDataType.Quadbin ||
      spatialDataSettings.spatialDataType === SpatialDataType.H3)
  )
}

/**
 * Sources have `spatialDataColumn` set in `src/features/.../cartoDeckGL.ts`),
 * but widgets do not necessarily use the same geo column name or type as the
 * layer. In tileset widgets `@carto/api-client` chooses a geo column name
 * automatically if 'storeGeometry' is enabled, but to avoid any guesswork,
 * Builder always overrides `spatialDataColumn` when calling
 * `setTileFeatureExtractOptions`.
 *
 * See: sc-487395, sc-487402, sc-487391.
 */
export function getTilesetWidgetGeoColumnName(dataset?: MapDatasetEnriched): string | undefined {
  if (!dataset) return undefined

  const parsedGeoColumn = parseGeoColumn(dataset?.geoColumn)
  const spatialDataColumn = parsedGeoColumn?.spatialDataColumn

  const isStaticTileset = dataset?.type === MapType.tileset
  const isStaticSpatialIndexTileset = isStaticSpatialIndexDataset(dataset)

  if (isStaticSpatialIndexTileset) {
    return getSchemeFromTilesetDataset(dataset)
  }

  if (isStaticTileset) {
    // TODO(c4r): It's awkward that we're importing a 'special' column name from
    // @carto/api-client, just to override `setTileFeatureExtractOptions({spatialDataColumn, ...})`
    // so that @carto/api-client never falls back to its own constant. In a
    // future release, make `setTileFeatureExtractOptions` require an explicit
    // `spatialDataColumn` value if `storeGeometry` is enabled, removing
    // FEATURE_GEOM_PROPERTY from the client.
    return FEATURE_GEOM_PROPERTY
  }

  if (!dataset?.metadata?.schema?.some((field) => caseInsensitiveEquals(field.name, spatialDataColumn))) {
    return undefined
  }
  return spatialDataColumn
}
